// Derived presentation metadata for the Action Centre v2 (LLM) cards (EO-1217).
// The backend returns raw card data; the category pill's colour is derived here on
// the frontend from the card's `cardType` — the column the backend documents as the
// one that "renders the pill" (ActionCentreCard.java).

export const CTA_ENUM = {
  CREATE_PROMO: 1,
  REVIEW_RATE_PLANS: 2,
  REVIEW_AVAILABILITY: 3,
};

// What the supplier did with a card, for POST /v2/recommendations/action. Both land
// in action_centre_card_state, in different columns, so dismissal rate stays separable
// from conversion — but only USED removes the card from the feed. DISMISS is recorded
// for reporting and hides the card for the session only, matching v1's Maybe Later.
// Must match the backend's ActionCentreCardActionType enum names exactly.
export const CARD_ACTION = {
  // "Maybe Later" / the X — the supplier passed on the card.
  DISMISS: "DISMISS",
  // The supplier acted on the CTA: created the promo, or followed the deep link.
  USED: "USED",
};

// Pill styling by card_type (Figma: Action Centre LLM Template, 4805:25902).
//
// card_type IS the tag — the pipeline writes the label and the backend documents the column as the
// one that renders the pill, so the frontend never decides WHICH pill a card gets, only how a given
// label looks. The design defines exactly two: Revenue Opportunity and Performance Alert, each with
// its own palette and icon.
const PILL_STYLE = {
  revenue_opportunity: { variant: "green", icon: "trending" },
  performance_alert: { variant: "red", icon: "pulse" },
};

// card_type is a free-form String, so the pipeline can emit a label we have no styling for. Blue is
// the design's neutral: the label still renders rather than being dropped.
const DEFAULT_STYLE = { variant: "blue", icon: "trending" };

// Only a blank card_type falls back to a generic label.
const NEUTRAL = {
  label: "Recommendation",
  variant: "blue",
  icon: "trending",
  slug: "other",
};

/** "Revenue Opportunity" -> "revenue_opportunity". Returns "" for blank input. */
const normalizeCardType = (cardType) =>
  String(cardType ?? "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    // Single `_`, not `_+`: the collapse above already reduced every run of
    // non-alphanumerics to ONE underscore, so consecutive ones cannot occur here.
    // The quantifier was therefore redundant, and `_+$` backtracks quadratically
    // on a long run of underscores.
    .replace(/^_|_$/g, "");

/**
 * Maps a card's `cardType` to its category pill. The backend documents two values
 * — "Revenue Opportunity" (green) and "Performance Alert" (blue) — but the column
 * is a free-form String written by the LLM pipeline, not an enum, so an unrecognised
 * value still renders its own text on the blue pill rather than being flattened into
 * the generic fallback. Only a missing/blank cardType falls back to "Recommendation".
 */
export const getCardCategory = (card = {}) => {
  const key = normalizeCardType(card?.cardType);
  if (!key) return NEUTRAL;

  const style = PILL_STYLE[key] || DEFAULT_STYLE;
  return {
    // Cast rather than trim the raw value: the column is free-form, so a non-string
    // here would throw mid-render and blank the page.
    label: String(card.cardType).trim(),
    variant: style.variant,
    icon: style.icon,
    slug: key,
  };
};

// Maps a card's deterministic `theme` to the promoCategory the create-promo flow
// needs. useCreatePromoState gates on `suggestionPromoId && promoCategory` to route
// a save through createSmartPromo, so without this the promo saves as a plain one
// and is never linked back to its card. The v1 mark-used side of that flag is
// replaced for v2 via the drawer's onRecommendationUsed callback — these cards
// record their own state in action_centre_card_state instead.
//
// Deliberately strict, unlike v1's `PROMO_CATEGORY_MAP[type] || type`: the backend's
// PromoActionService.markUsed throws on any type outside LOS/DOW/ALIGNMENT/LEAD_TIME,
// so an unmapped theme (seasonal, …) must yield null and fall back to a plain save
// rather than send a value the mark-used call would reject.
const PROMO_CATEGORY_BY_THEME = {
  los: "LOS",
  dow: "DOW",
  lead_time: "LEAD_TIME",
  // Lowercase on purpose — markSmartPromoRecommendationUsed branches on
  // `promoCategory === "alignment"` to hit updatePromoUsed instead of mark-used.
  alignment: "alignment",
  alignment_intelligence: "alignment",
  // EO-1891: tags the promo for Black Friday attribution. Safe despite the
  // mark-used note above: v2 cards record their own state, so mark-used is
  // never called with it.
  black_friday: "BLACK_FRIDAY",
};

/** Returns null when the card's theme has no create-promo category. */
export const getPromoCategory = (card = {}) =>
  PROMO_CATEGORY_BY_THEME[
    String(card?.theme ?? "")
      .trim()
      .toLowerCase()
  ] ?? null;

/** e.g. 24300 -> "$24,300/mth". Returns null when there is no amount to show. */
export const formatRevenue = (value) => {
  if (value == null || Number.isNaN(Number(value))) return null;
  return `$${Math.round(Number(value)).toLocaleString()}/mth`;
};

/** e.g. 12.5 -> "12.5%". Returns null when there is no percentage to show. */
export const formatPercent = (value) => {
  if (value == null || Number.isNaN(Number(value))) return null;
  const rounded = Math.round(Number(value) * 10) / 10;
  return `${rounded}%`;
};
